




import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaEdit, FaEye, FaEyeSlash, FaTimes } from "react-icons/fa";
import axiosInstance from "../api/axiosInstance";

export default function Agents() {
  const navigate = useNavigate();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Modal states
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [updatingPassword, setUpdatingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState("");

  const user = JSON.parse(localStorage.getItem("user"));
  const isAdmin = user?.role === "admin";

  // ── FETCH USERS ─────────────────────────────────────
  useEffect(() => {
    if (!isAdmin) return;

    const fetchUsers = async () => {
      try {
        setLoading(true);

        const response = await axiosInstance.get(
          "/users/agents-quality"
        );

        setUsers(response.data.data || []);
      } catch (err) {
        console.error("Fetch Users Error:", err);

        setError(
          err.response?.data?.message ||
            "Failed to load users."
        );
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, [isAdmin]);

  // ── OPEN PASSWORD MODAL ─────────────────────────────
  const handleEditPassword = (e, selectedAgent) => {
    e.stopPropagation();

    setSelectedUser(selectedAgent);

    setNewPassword("");
    setConfirmPassword("");
    setPasswordError("");
    setPasswordSuccess("");

    setShowPasswordModal(true);
  };

  // ── CLOSE PASSWORD MODAL ────────────────────────────
  const closePasswordModal = () => {
    if (updatingPassword) return;

    setShowPasswordModal(false);
    setSelectedUser(null);

    setNewPassword("");
    setConfirmPassword("");
    setPasswordError("");
    setPasswordSuccess("");
  };

  // ── UPDATE PASSWORD ─────────────────────────────────
  const handleUpdatePassword = async (e) => {
    e.preventDefault();

    setPasswordError("");
    setPasswordSuccess("");

    if (!newPassword) {
      setPasswordError("Please enter a new password.");
      return;
    }

    if (newPassword.length < 6) {
      setPasswordError(
        "Password must be at least 6 characters."
      );
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError(
        "New password and confirm password do not match."
      );
      return;
    }

    try {
      setUpdatingPassword(true);

      const response = await axiosInstance.put(
        `users/${selectedUser.id}/password`,
        {
          password: newPassword,
        }
      );

      setPasswordSuccess(
        response.data?.message ||
          "Password updated successfully."
      );

      // Update password in local table
      setUsers((prevUsers) =>
        prevUsers.map((u) =>
          u.id === selectedUser.id
            ? {
                ...u,
                password: newPassword,
              }
            : u
        )
      );

      // Close modal after success
      setTimeout(() => {
        setShowPasswordModal(false);
        setSelectedUser(null);
        setNewPassword("");
        setConfirmPassword("");
        setPasswordSuccess("");
      }, 1000);
    } catch (err) {
      console.error("Update Password Error:", err);

      setPasswordError(
        err.response?.data?.message ||
          "Failed to update password."
      );
    } finally {
      setUpdatingPassword(false);
    }
  };

  // ── ACCESS DENIED ────────────────────────────────────
  if (!isAdmin) {
    return (
      <div className="p-6 bg-gray-100 min-h-screen">
        <div className="bg-white rounded-xl shadow p-10 text-center text-red-500">
          Access denied. Admins only.
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-100 min-h-screen">

      {/* HEADER */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-slate-800">
            Agents & Quality Team
          </h1>

          <p className="text-sm text-slate-500 mt-1">
            Total: {users.length}
          </p>
        </div>
      </div>

      {/* TABLE */}
      <div className="bg-white rounded-xl shadow overflow-hidden">

        {loading ? (
          <div className="p-10 text-center text-slate-500">
            Loading...
          </div>
        ) : error ? (
          <div className="p-10 text-center text-red-500">
            {error}
          </div>
        ) : users.length === 0 ? (
          <div className="p-10 text-center text-slate-400">
            No users found
          </div>
        ) : (
          <table className="w-full text-left text-sm">

            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <th className="p-4 font-semibold">ID</th>
                <th className="p-4 font-semibold">Name</th>
                <th className="p-4 font-semibold">Email</th>
                <th className="p-4 font-semibold">Role</th>
                <th className="p-4 font-semibold">Status</th>
                <th className="p-4 font-semibold">Password</th>
              </tr>
            </thead>

            <tbody>
              {users.map((u) => (
                <tr
                  key={u.id}
                  className="border-t hover:bg-gray-50 cursor-pointer transition"
                  onClick={() =>
                    navigate(`/agents/${u.id}`)
                  }
                >
                  <td className="p-4 text-blue-600 font-medium">
                    #{u.id}
                  </td>

                  <td className="p-4 text-slate-800">
                    {u.first_name}{" "}
                    {u.last_name &&
                    u.last_name !== "—"
                      ? u.last_name
                      : ""}
                  </td>

                  <td className="p-4 text-slate-600">
                    {u.email || "—"}
                  </td>

                  <td className="p-4">
                    <span
                      className={`px-2.5 py-1 text-xs font-semibold rounded-full capitalize ${
                        u.role === "agent"
                          ? "bg-blue-100 text-blue-700"
                          : "bg-purple-100 text-purple-700"
                      }`}
                    >
                      {u.role}
                    </span>
                  </td>

                  <td className="p-4">
                    <span
                      className={`px-2.5 py-1 text-xs font-semibold rounded-full ${
                        u.is_active
                          ? "bg-green-100 text-green-700"
                          : "bg-gray-200 text-gray-600"
                      }`}
                    >
                      {u.is_active
                        ? "Active"
                        : "Inactive"}
                    </span>
                  </td>

                  {/* PASSWORD */}
                  <td className="p-4">
                    <div className="flex items-center gap-3">

                      <span className="text-slate-500">
                        {u.password || "—"}
                      </span>

                      <button
                        type="button"
                        title="Edit Password"
                        onClick={(e) =>
                          handleEditPassword(e, u)
                        }
                        className="text-blue-600 hover:text-blue-800 hover:bg-blue-50 p-2 rounded-lg transition"
                      >
                        <FaEdit size={15} />
                      </button>

                    </div>
                  </td>
                </tr>
              ))}
            </tbody>

          </table>
        )}
      </div>

      {/* =====================================================
          PASSWORD MODAL
      ===================================================== */}
      {showPasswordModal && selectedUser && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={closePasswordModal}
        >
          <div
            className="bg-white w-full max-w-md rounded-2xl shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >

            {/* MODAL HEADER */}
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <div>
                <h2 className="text-lg font-semibold text-slate-800">
                  Edit Password
                </h2>

                <p className="text-sm text-slate-500 mt-1">
                  {selectedUser.first_name}{" "}
                  {selectedUser.last_name || ""}
                </p>
              </div>

              <button
                type="button"
                onClick={closePasswordModal}
                className="text-slate-400 hover:text-slate-700 p-2 rounded-lg hover:bg-gray-100"
              >
                <FaTimes />
              </button>
            </div>

            {/* MODAL BODY */}
            <form
              onSubmit={handleUpdatePassword}
              className="p-6"
            >

              {/* ERROR */}
              {passwordError && (
                <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-600 text-sm">
                  {passwordError}
                </div>
              )}

              {/* SUCCESS */}
              {passwordSuccess && (
                <div className="mb-4 p-3 rounded-lg bg-green-50 border border-green-200 text-green-600 text-sm">
                  {passwordSuccess}
                </div>
              )}

              {/* NEW PASSWORD */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  New Password
                </label>

                <div className="relative">
                  <input
                    type={
                      showNewPassword
                        ? "text"
                        : "password"
                    }
                    value={newPassword}
                    onChange={(e) =>
                      setNewPassword(e.target.value)
                    }
                    placeholder="Enter new password"
                    className="w-full border border-gray-300 rounded-lg px-4 py-2.5 pr-11 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />

                  <button
                    type="button"
                    onClick={() =>
                      setShowNewPassword(
                        !showNewPassword
                      )
                    }
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showNewPassword ? (
                      <FaEyeSlash />
                    ) : (
                      <FaEye />
                    )}
                  </button>
                </div>
              </div>

              {/* CONFIRM PASSWORD */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Confirm Password
                </label>

                <div className="relative">
                  <input
                    type={
                      showConfirmPassword
                        ? "text"
                        : "password"
                    }
                    value={confirmPassword}
                    onChange={(e) =>
                      setConfirmPassword(
                        e.target.value
                      )
                    }
                    placeholder="Confirm new password"
                    className="w-full border border-gray-300 rounded-lg px-4 py-2.5 pr-11 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />

                  <button
                    type="button"
                    onClick={() =>
                      setShowConfirmPassword(
                        !showConfirmPassword
                      )
                    }
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showConfirmPassword ? (
                      <FaEyeSlash />
                    ) : (
                      <FaEye />
                    )}
                  </button>
                </div>
              </div>

              {/* BUTTONS */}
              <div className="flex justify-end gap-3">

                <button
                  type="button"
                  onClick={closePasswordModal}
                  disabled={updatingPassword}
                  className="px-5 py-2.5 rounded-lg border border-gray-300 text-slate-600 hover:bg-gray-50 transition disabled:opacity-50"
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  disabled={updatingPassword}
                  className="px-5 py-2.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition disabled:opacity-50"
                >
                  {updatingPassword
                    ? "Updating..."
                    : "Update Password"}
                </button>

              </div>

            </form>
          </div>
        </div>
      )}
    </div>
  );
}


